<script lang="ts">
	import { getContext, onMount, tick } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { ConversionType, RenderMode } from "#gen/bundles/v1/bundle_pb.js";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import { FieldType } from "#gen/forms/v1/field_pb.js";
	import { FormSchema, type Form } from "#gen/forms/v1/forms_pb.js";
	import { getValidator } from "#lib/ajv.js";
	import type { ComponentProps } from "#lib/components/page/ComponentProps.js";
	import Address from "#lib/components/page/components/form/Address.svelte";
	import Choice from "#lib/components/page/components/form/Choice.svelte";
	import Date from "#lib/components/page/components/form/Date.svelte";
	import EmailField from "#lib/components/page/components/form/EmailField.svelte";
	import FileUpload from "#lib/components/page/components/form/FileUpload.svelte";
	import Heading from "#lib/components/page/components/form/Heading.svelte";
	import NameField from "#lib/components/page/components/form/NameField.svelte";
	import NumberField from "#lib/components/page/components/form/NumberField.svelte";
	import PhoneField from "#lib/components/page/components/form/PhoneField.svelte";
	import PolicyField from "#lib/components/page/components/form/PolicyField.svelte";
	import TextField from "#lib/components/page/components/form/TextField.svelte";
	import { formLayout } from "#lib/components/page/formLayout.js";
	import { fromJson } from "#lib/protobuf-compat.js";
	import { objectValue, structValue } from "#lib/struct.js";
	import { isLight } from "#lib/theme.js";
	import Button from "#lib/ui/Button/index.js";
	import type { ValidateFunction } from "ajv";

	type FieldComponent = TextField | EmailField | NameField | PhoneField | Address | Choice | Date | FileUpload | NumberField | PolicyField;
	type FileUploadField = { url: string; fileName?: string; mimetype?: string };
	type AddressField = Record<string, string | undefined> | null;

	const execute: (conversionType: ConversionType, conversionComponent: string, sourceAnchor?: string) => Promise<boolean> = getContext("execute");

	let { id, style, mode, page, data, renderMode, readOnly = false, connectable = false, connectionRequired = false }: ComponentProps = $props();

	const components: Record<string, FieldComponent> = $state({});
	let fieldValues: Record<string, string | (string[] & string) | undefined> = $state({});
	let submitting = $state(false);
	let uploading = $state(false);
	let isSafari = $state(false);
	let fieldValidators: Record<string, ValidateFunction[]> | undefined = $state();
	let formReady = $state(false);

	const useSafariFix = $derived(isSafari && mode !== Mode.LIVE);
	const form = $derived(fromJson(FormSchema, objectValue(structValue($data, id), "form") ?? {}, { ignoreUnknownFields: true }));
	const buttonText = $derived(form.actionText ? form.actionText : "Submit");

	// Sticky submit button is page-tree config, defaulting to on for legacy forms. See formLayout.
	const stickySubmitButton = $derived(formLayout($page, id).stickySubmitButton);

	// Make sure the selected color in an input has enough contrast with the glyphs (checkbox, radio dot) that appear on top of it.
	function selectedGlyphVars(styleStr: string | undefined): string {
		const selected = styleStr?.match(/--form-selected-color:\s*([^;]+)/i)?.[1]?.trim() || "#111928";
		// A light fill needs a dark glyph and vice versa. The checkbox glyph is a background-image whose
		// stroke takes the URL-encoded hex, while the radio dot is flowbite's ::after (a plain color).
		const glyphDark = isLight(selected);
		const glyphEnc = glyphDark ? "%23111928" : "%23ffffff";
		const glyphHex = glyphDark ? "#111928" : "#ffffff";
		const check = `url("data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20fill='none'%20viewBox='0%200%2024%2024'%3e%3cpath%20stroke='${glyphEnc}'%20stroke-linecap='round'%20stroke-linejoin='round'%20stroke-width='2'%20d='M5%2011.917%209.724%2016.5%2019%207.5'/%3e%3c/svg%3e")`;
		return `--form-check-image:${check};--form-glyph-color:${glyphHex}`;
	}

	// Legacy forms also carry appearance on the <form>, but only once the user has set
	// it. An untouched legacy form has an empty style and must render exactly as before.
	const hasAppearance = $derived(connectable || !!style);
	const formStyle = $derived(hasAppearance ? [style, selectedGlyphVars(style)].filter(Boolean).join(";") : undefined);

	onMount(() => {
		isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
	});

	$effect(() => {
		(async () => {
			await compileValidators(form);
			await tick();
			formReady = true;
		})();
	});

	export function value(out: Record<string, unknown>) {
		out[id] = fieldValues;
	}

	export async function validate(): Promise<boolean> {
		let valid = true;
		for (const field of form.fields) {
			valid = ((await components[field.name]?.validate()) ?? true) && valid;
		}
		return valid;
	}

	async function compileValidators(form: Form) {
		if (!form.rules) {
			fieldValidators = {};
			return;
		}
		const validators: [string[], ValidateFunction][] = await Promise.all(form.rules.map(async r => [r.fieldKeys, await getValidator(r.schema)]));
		fieldValidators = validators.reduce(
			(agg, [keys, validator]) => {
				keys.forEach(v => {
					if (!agg[v]) agg[v] = [];
					agg[v].push(validator);
				});
				return agg;
			},
			{} as Record<string, ValidateFunction[]>,
		);
	}
	function normalizeValues(values: Record<string, unknown>): Record<string, unknown> {
		const normalized: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(values)) {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				const vals = Object.values(value);
				const isEmptyObject = vals.every(v => v === "" || v === null || v === undefined);

				normalized[key] = isEmptyObject ? undefined : value;
			} else {
				normalized[key] = value;
			}
		}

		return normalized;
	}
	function checkFieldConditions(name: string, validators: Record<string, ValidateFunction<unknown>[]> | undefined, values: Record<string, unknown>): boolean {
		if (!validators) return false; // wait for schemas to compile
		if (!validators[name]) return true; // return true if field has no schemas
		const normalized = normalizeValues(values); // normalize values for empty object handling
		return validators[name].every(v => v(normalized)); // check field against all schemas
	}

	function handleFormSubmit(event: Event) {
		event.preventDefault();
		if (readOnly || submitting || uploading) {
			return;
		}
		submit();
	}

	async function submit(): Promise<void> {
		submitting = true;
		try {
			// A connectable form submits with its own id as the sourceAnchor for element-level
			// routing. Page.svelte skips validation for anchored submits, so validate here first.
			// Legacy (non-connectable) forms submit with no anchor and Page.svelte validates them.
			if (connectable && !(await validate())) {
				return;
			}
			let success = false;
			if (connectable) {
				success = await execute(ConversionType.FORM_SUBMISSION, id, id);
			} else {
				success = await execute(ConversionType.UNSPECIFIED, id);
			}
			if (success) {
				fieldValues = {};
			}
		} catch (_: unknown) {
			// TODO: Use the error here to show a helpful message to the user
		} finally {
			submitting = false;
		}
	}
</script>

{#snippet submitButton()}
	<Button type="submit" data-action-name="form-submit-btn" data-flow-button disabled={!formReady || readOnly || submitting || uploading} color="dark" class={twMerge("btn-consumer-primary relative p-3.5", readOnly && "disabled:pointer-events-none disabled:cursor-pointer disabled:opacity-100")}>
		{buttonText}
	</Button>
{/snippet}

<form class="relative flex w-full grow flex-col" class:form-appearance={hasAppearance} style={formStyle} onsubmit={handleFormSubmit} data-linkable-id={connectable ? id : undefined} data-connection-required={connectable ? String(connectionRequired) : undefined}>
	<div class="consumer grow py-2">
		{#each form.fields as field (field)}
			{#if checkFieldConditions(field.name, fieldValidators, fieldValues)}
				{@const fieldId = `${id}-field-${field.name}`}
				<div class="mb-4">
					{#if field.fieldType === FieldType.BLOCK_TEXT}
						<Heading id={fieldId} {field} {mode} />
					{:else if field.fieldType === FieldType.TEXT}
						<TextField id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.EMAIL}
						<EmailField id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.NAME}
						<NameField id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.PHONE}
						<PhoneField id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.ADDRESS}
						<Address id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name] as unknown as AddressField} {field} {readOnly} />
					{:else if field.fieldType === FieldType.CHOICE}
						<Choice id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.DATE}
						<Date id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.FILE_UPLOAD}
						<FileUpload id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name] as unknown as FileUploadField[] | null} {field} {readOnly} bind:loading={uploading} />
					{:else if field.fieldType === FieldType.NUMBER}
						<NumberField id={fieldId} bind:this={components[field.name]} bind:value={fieldValues[field.name]} {field} {readOnly} />
					{:else if field.fieldType === FieldType.LEGAL}
						<PolicyField id={fieldId} bind:this={components[field.name]} value={fieldValues[field.name]} {field} {readOnly} />
					{/if}
				</div>
			{/if}
		{/each}
	</div>
	{#if mode === Mode.EDITING || renderMode === RenderMode.INLINE}
		{@render submitButton()}
	{:else}
		<!-- Set '-bottom-[55.5%]' class to address bug in safari rendering in preview mode -->
		<div class="overflow-hidden py-4" class:sticky={stickySubmitButton} class:bottom-0={stickySubmitButton && !useSafariFix} class:-bottom-[55.5%]={stickySubmitButton && useSafariFix}>
			{#if stickySubmitButton}
				<div class="absolute top-0 right-0 bottom-0 left-0"></div>
			{/if}
			{@render submitButton()}
		</div>
	{/if}
</form>

<style>
	/*
	 * Connectable forms carry their appearance on component.style, applied inline to the <form>.
	 * Container CSS keys (background-color, border, radius, padding) style the <form> directly.
	 * Input/submit/misc overrides arrive as --form-* custom properties. These scoped rules point
	 * them at the same fields app.css's global `.consumer` rules style, winning on specificity via
	 * the scoped `form.form-appearance` prefix. The var() fallbacks match the `.consumer` defaults,
	 * so an unstyled form renders identically to today.
	 */

	/*
	 * Container defaults. The form's own background/border/radius/padding come from inline keys on
	 * component.style (DEFAULT_FORM_STYLE + edits), which win over these. When a container key is reset
	 * (removed), the form falls back to these defaults instead of going transparent — keeping the render
	 * consistent with the value the color picker shows.
	 */
	form.form-appearance {
		background-color: #ffffff;
		border: 1px solid #d1d5db;
		border-radius: 16px;
		padding: 12px;
	}

	form.form-appearance :global(input:not([type="checkbox"]):not([type="radio"])),
	form.form-appearance :global(select),
	form.form-appearance :global(textarea) {
		background-color: var(--form-input-background-color, #f9fafb);
		border-color: var(--form-input-border-color, #d1d5db);
		color: var(--form-input-text-color, #111827);
		border-radius: var(--form-input-border-radius, 0.5rem);
	}

	form.form-appearance :global(input:not([type="checkbox"]):not([type="radio"]):focus),
	form.form-appearance :global(select:focus),
	form.form-appearance :global(textarea:focus) {
		border-color: var(--form-input-border-color, #6b7280);
		box-shadow: 0 0 0 1px var(--form-input-border-color, #6b7280);
	}

	form.form-appearance :global(input::placeholder),
	form.form-appearance :global(textarea::placeholder) {
		color: var(--form-input-placeholder-color, #9ca3af);
	}
	/* Date inputs render their MM/DD/YYYY hint through webkit's datetime-edit sub-pseudos (see app.css),
	 * not ::placeholder, so recolor each of them to match the placeholder color. `.date-empty` marks a
	 * field with no value in both the editor preview and the live consumer, and Date.svelte drops it once
	 * a value is entered so filled dates keep their text color rather than the placeholder color. */
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit),
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit-fields-wrapper),
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit-text),
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit-month-field),
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit-day-field),
	form.form-appearance :global(input[type="date"].date-empty::-webkit-datetime-edit-year-field) {
		color: var(--form-input-placeholder-color, #9ca3af) !important;
	}

	/* Choice fields (radio / multi-select / single checkbox) wrap their options in a bordered <ul>. Match
	 * its background, border, radius, and the dividers between options to the inputs so choices look
	 * consistent with the text fields. */
	form.form-appearance :global(ul.divide-y) {
		background-color: var(--form-input-background-color, #f9fafb);
		border-color: var(--form-input-border-color, #d1d5db);
		border-radius: var(--form-input-border-radius, 0.5rem);
	}
	form.form-appearance :global(ul.divide-y > *) {
		border-color: var(--form-input-border-color, #d1d5db);
	}
	/* Option labels are the field's content (like typed input text), not field titles, so use the input
	 * text color instead of the label color that the generic label rule below would otherwise apply. */
	form.form-appearance :global(ul.divide-y label) {
		color: var(--form-input-text-color, #111827);
	}

	/* Camera capture dropzone / preview (FileUpload) is not a native <input>, so it needs the same
	 * input tokens as text fields. Target the text/icon classes directly — Tailwind text-* utilities
	 * and `.consumer label` would otherwise keep gray fixed colors. */
	form.form-appearance :global(.file-capture-target) {
		border-color: var(--form-input-border-color, #d1d5db);
		border-radius: var(--form-input-border-radius, 0.5rem);
		background-color: var(--form-input-background-color, #f9fafb);
	}
	form.form-appearance :global(.file-capture-target:focus-within) {
		border-color: var(--form-input-border-color, #6b7280);
		box-shadow: 0 0 0 1px var(--form-input-border-color, #6b7280);
	}
	form.form-appearance :global(.file-capture-target .file-capture-target-label) {
		color: var(--form-input-text-color, #111827);
	}
	form.form-appearance :global(.file-capture-target .file-capture-target-icon) {
		color: var(--form-input-icon-color, #6b7280);
	}

	/*
	 * The dropdown chevron is a fixed-color background SVG that a CSS variable can't tint, and a <select>
	 * can't hold a child. So hide it and paint our own chevron over the select's wrapper, colored
	 * by --form-input-icon-color via a mask. Searchable choice uses the same Flowbite glyph as an SVG
	 * and only needs the icon color token.
	 */
	form.form-appearance :global(select) {
		background-image: none;
	}
	form.form-appearance :global(div:has(> select)) {
		position: relative;
	}
	form.form-appearance :global(div:has(> select))::after {
		content: "";
		position: absolute;
		top: 50%;
		right: 0.75rem;
		width: 0.7rem;
		height: 0.45rem;
		transform: translateY(-50%);
		pointer-events: none;
		background-color: var(--form-input-icon-color, #6b7280);
		mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e%3cpath stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 1 4 4 4-4'/%3e%3c/svg%3e") no-repeat center / contain;
		-webkit-mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e%3cpath stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 1 4 4 4-4'/%3e%3c/svg%3e") no-repeat center / contain;
	}
	form.form-appearance :global(.form-select-chevron) {
		color: var(--form-input-icon-color, #6b7280);
	}

	form.form-appearance :global(.form-choice-listbox) {
		background-color: var(--form-input-background-color, #f9fafb);
		border-color: var(--form-input-border-color, #d1d5db);
		color: var(--form-input-text-color, #111827);
		border-radius: var(--form-input-border-radius, 0.5rem);
	}
	form.form-appearance :global(.form-choice-listbox button) {
		color: var(--form-input-text-color, #111827);
	}
	form.form-appearance :global(.form-choice-listbox [aria-selected="true"]) {
		background-color: color-mix(in srgb, var(--form-input-text-color, #111827) 8%, var(--form-input-background-color, #f9fafb));
	}

	/*
	 * Date field calendar icon. The native calendar-picker button (shown only when the input is enabled,
	 * i.e. the consumer) is a fixed-color glyph, so mask it and paint --form-input-icon-color through it.
	 * The builder preview renders the input disabled, where the browser hides that button entirely, so
	 * paint a matching static calendar over the wrapper. Both use the same icon and color for parity.
	 */
	form.form-appearance :global(input[type="date"]::-webkit-calendar-picker-indicator) {
		cursor: pointer;
		opacity: 1;
		background-color: var(--form-input-icon-color, #6b7280);
		mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3crect x='3' y='4' width='18' height='18' rx='2'/%3e%3cpath d='M16 2v4M8 2v4M3 10h18'/%3e%3c/svg%3e") no-repeat center / contain;
		-webkit-mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3crect x='3' y='4' width='18' height='18' rx='2'/%3e%3cpath d='M16 2v4M8 2v4M3 10h18'/%3e%3c/svg%3e") no-repeat center / contain;
	}
	form.form-appearance :global(div:has(> input[type="date"].date-disabled))::after {
		content: "";
		position: absolute;
		top: 50%;
		right: 0.75rem;
		width: 1rem;
		height: 1rem;
		transform: translateY(-50%);
		pointer-events: none;
		background-color: var(--form-input-icon-color, #6b7280);
		mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3crect x='3' y='4' width='18' height='18' rx='2'/%3e%3cpath d='M16 2v4M8 2v4M3 10h18'/%3e%3c/svg%3e") no-repeat center / contain;
		-webkit-mask: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3crect x='3' y='4' width='18' height='18' rx='2'/%3e%3cpath d='M16 2v4M8 2v4M3 10h18'/%3e%3c/svg%3e") no-repeat center / contain;
	}

	form.form-appearance :global(label),
	form.form-appearance :global(.form-label) {
		color: var(--form-label-color, #111827);
	}

	/* The secondary line under a field's title (field.description). */
	form.form-appearance :global(.field-description) {
		color: var(--form-description-color, #6b7280);
	}

	/* Static Header text element (Heading.svelte). */
	form.form-appearance :global(.form-header-text) {
		color: var(--form-header-color, #000000);
	}

	/* Terms and conditions legal text (PolicyField.svelte). Set on the wrapper so the rendered policy
	 * HTML inherits it, since that markup carries no color of its own. */
	form.form-appearance :global(.form-terms-text) {
		color: var(--form-terms-color, #111928);
	}

	/* Terms and conditions links: the standalone URL-variant link plus any links embedded in the rich
	 * policy text. HtmlViewer paints embedded links blue through its own global rule, so this more
	 * specific selector overrides it. */
	form.form-appearance :global(.form-terms-link),
	form.form-appearance :global(.form-terms-text a) {
		color: var(--form-terms-link-color, #155dfc);
	}
	/* The URL-variant link inherits the base 16px while the rich policy text renders at 12px, so pin the
	 * link to match for a consistent terms & conditions size. */
	form.form-appearance :global(.form-terms-link) {
		font-size: 0.75rem;
	}

	/* These render with appearance:none (@tailwindcss/forms), so the checked fill is the control's
	 * currentColor, not accent-color, which the browser ignores here. Drive the fill with `color`. Keep
	 * accent-color set as well for any browser that still paints the control natively. */
	form.form-appearance :global(input[type="checkbox"]),
	form.form-appearance :global(input[type="radio"]) {
		color: var(--form-selected-color, #111928);
		accent-color: var(--form-selected-color, #111928);
		border-color: var(--form-input-checkbox-border-color, #d1d5db);
	}
	/* Unchecked interior. Scoped to :not(:checked) so it doesn't override the checked fill, which
	 * @tailwindcss/forms paints with currentColor (the selected color) on :checked. */
	form.form-appearance :global(input[type="checkbox"]:not(:checked)),
	form.form-appearance :global(input[type="radio"]:not(:checked)) {
		background-color: var(--form-input-checkbox-background-color, #f9fafb);
	}
	/* Swap the checked glyph for one that contrasts the fill (see selectedGlyphVars) so a light Selected
	 * color keeps a visible checkmark/dot instead of a baked-in white one. The checkbox glyph is a
	 * background-image. The radio's dot is flowbite's ::after, so recolor that. */
	form.form-appearance :global(input[type="checkbox"]:checked) {
		background-image: var(--form-check-image);
	}
	form.form-appearance :global(input[type="radio"]:checked::after) {
		background-color: var(--form-glyph-color, #ffffff);
		/* Flowbite pins the dot with a fixed 7px offset that sits ~1px off-centre on the 16px control, so
		 * it reads as clipped against the ring. Recentre it robustly instead. */
		inset: 0;
		margin: auto;
		transform: none;
	}
	/* Flowbite stacks a larger ::before circle under the dot, tinted by an unset var. Keep it clear so it
	 * can never reappear as a mismatched ring behind the contrast dot. */
	form.form-appearance :global(input[type="radio"]:checked::before) {
		background-color: transparent;
	}

	/* Recolor flowbite's red error state (input borders + helper text) to the chosen error color. */
	form.form-appearance :global([class*="text-red"]) {
		color: var(--form-error-color, #e02424) !important;
	}
	form.form-appearance :global([class*="border-red"]) {
		border-color: var(--form-error-color, #e02424) !important;
	}

	/* Submit button lives outside the .consumer wrapper, so target it explicitly. */
	form.form-appearance :global(.btn-consumer-primary) {
		background-color: var(--form-submit-background-color, #1f2a37);
		color: var(--form-submit-text-color, #ffffff);
		border: 1px solid var(--form-submit-border-color, #1f2a37);
		border-radius: var(--form-submit-border-radius, 16px);
	}
	form.form-appearance :global(.btn-consumer-primary:hover:not(:disabled)) {
		filter: brightness(0.92);
	}
</style>
